import * as React from 'react';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';

import Spinner from 'components/spinner/Spinner';
import Tooltip from 'components/Tooltip';
import CostAttributionMissing from '../cost-attribution-missing/CostAttributionMissing';
import EmptyMessage from 'modules/reporting/components/empty-message/EmptyMessage';

import { renderPrice } from 'utils/helper/price';
import { __t, applyTranslations } from 'utils/language';
import { getMetricValueInPercentage } from 'utils/helper/common';

import { ReportingDeliverableTypes } from 'modules/reporting/types/ReportingMetaInfo.type';

applyTranslations(require('./campaign-budget-by-task-widget.i18n.json'));

import './campaign-budget-by-task-widget.scss';

type Props = {
  recap: null | {
    budget: number | undefined | null,
    roi: number
  },
  campaignId: number,
  taskType: ReportingDeliverableTypes,
  hasDefinedAttribution: boolean,
  view?: 'small' | 'normal',
  isLoading: boolean
};

const CampaignBudgetByTaskWidget = (props: Props): JSX.Element => {
  const { recap, campaignId, taskType, hasDefinedAttribution, isLoading, view } = props;
  const { roi, budget } = recap || {};

  const renderSpinner = (): JSX.Element => (
    <div className="campaign-budget-by-task-widget__spinner">
      <Spinner
        position="inline"
        size="small"
        color="light"
      />
    </div>
  );

  const renderRequestCostContainer = () => (
    <CostAttributionMissing campaignId={campaignId} />
  );

  const renderBudget = () => (
    <React.Fragment>
      {
        !isEmpty(recap) ? (
          <div className="campaign-budget-by-task-widget__budget-roi-container">
            <div className="campaign-budget-by-task-widget__budget">
              <span>{budget ? renderPrice(budget) : 'n/a'}</span>
            </div>
            <div className="campaign-budget-by-task-widget__roi">
              <div className="campaign-budget-by-task-widget__roi-value">
                {getMetricValueInPercentage(roi, { defaultValue: 'n/a', hasThousandsSeparator: true })}
              </div>
              <div className="campaign-budget-by-task-widget__roi-label">
                {__t(`campaign_budget_by_task_widget.${taskType}.total_roi_title`)}
                <Tooltip
                  message={__t(`campaign_budget_by_task_widget.${taskType}.roi_tooltip`)}
                  width="long"
                  showQuestionMark
                  classNameWrapper="campaign-budget-by-task-widget__tooltip"
                />
              </div>
            </div>
          </div>
        ) : <EmptyMessage />
      }
      <div className="campaign-budget-by-task-widget__info">
        {__t('campaign_budget_by_task_widget.budget_disclaimer')}
        <a
          color="light"
          rel="noopener noreferrer"
          target="_blank"
          href={`/campaigns/${campaignId}/report/v2/cca`}
          className="campaign-budget-by-task-widget__link"
        >
          {__t('campaign_budget_by_task_widget.cca_link')}
        </a>
      </div>
    </React.Fragment>
  );

  const renderBody = (): JSX.Element => {
    if (hasDefinedAttribution) {
      return renderBudget();
    }

    return renderRequestCostContainer();
  };

  const containerClasses = classnames({
    'campaign-budget-by-task-widget': true,
    'campaign-budget-by-task-widget--no-budget': !hasDefinedAttribution,
    [`campaign-budget-by-task-widget--${view}`]: view
  });

  return (
    <div className={containerClasses}>
      <div className="campaign-budget-by-task-widget__title">
        {__t(`campaign_budget_by_task_widget.${taskType}.title`)}
        {
          hasDefinedAttribution && (
            <Tooltip
              message={__t(`campaign_budget_by_task_widget.${taskType}.budget_tooltip`)}
              width="long"
              showQuestionMark
              classNameWrapper="reporting-statistic-table__tooltip"
            />
          )
        }
      </div>
      {
        isLoading
          ? renderSpinner()
          : renderBody()
      }
    </div>
  );
};

export default CampaignBudgetByTaskWidget;
